import React, { ReactElement, useCallback } from "react";
import { Stack, styled, SxProps, IconButton, Tooltip, Button } from "@mui/material";
import { StartOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import {
  AddToSequenceFromPreview,
  GetLinkedToPreviewActionId,
  GetPreviewFps,
  GetPreviewIsPlaying,
  GetPreviewTime,
  SetInFrame,
  SetOutFrame,
  TogglePreviewIsPlaying,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useSelector } from "react-redux";
import TimetoolPreviewTimeline from "./timetool-preview-timeline";
import { useTranslation } from "react-i18next";

type SetMarkerFromPlayerCurrentFrameProps = {
  trucToExecute;
  tooltip: string;
  icon: ReactElement;
};

export const SetMarkerFromPlayerCurrentFrame: React.FC<SetMarkerFromPlayerCurrentFrameProps> = (props) => {
  const [tooltipVisibility, setTooltipVisibility] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const previewTime = useSelector(GetPreviewTime);
  const previewFps = useSelector(GetPreviewFps);

  return (
    <Tooltip open={tooltipVisibility} arrow title={props.tooltip}>
      <IconButton
        onClick={() => {
          if (props.trucToExecute === "In") {
            dispatch(SetInFrame(Math.round(previewTime * previewFps)));
          } else {
            dispatch(SetOutFrame(Math.round(previewTime * previewFps)));
          }
        }}
        onMouseOver={() => setTooltipVisibility(true)}
        onMouseLeave={() => setTooltipVisibility(false)}
        color="default"
      >
        {props.icon}
      </IconButton>
    </Tooltip>
  );
};

export type TimetoolSequenceEditorProps = {
  sx?: SxProps;
};

const TimetoolSequenceEditor: React.FC<TimetoolSequenceEditorProps> = (props) => {
  const { t } = useTranslation();
  const { ...otherProps } = props;
  const dispatch = useDispatch();
  const linkedAction = useSelector(GetLinkedToPreviewActionId);

  const handleAddToSequence = useCallback(() => {
    dispatch(AddToSequenceFromPreview());
  }, []);

  const isPlaying = useSelector(GetPreviewIsPlaying);
  const [tooltipVisibility, setTooltipVisibility] = React.useState<boolean>(false);

  return (
    <Stack flexDirection="row" alignItems="center" {...otherProps}>
      <LeftControls direction="row" alignItems="center" spacing={0.5}>
        <IconButton onClick={() => dispatch(TogglePreviewIsPlaying())}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <SetMarkerFromPlayerCurrentFrame
          trucToExecute={"In"}
          icon={<StartOutlined />}
          tooltip={t("tooltip:title.markInTimecode")}
        />
        <SetMarkerFromPlayerCurrentFrame
          trucToExecute={"Out"}
          icon={<StartOutlined sx={{ transform: "rotate(180deg)" }} />}
          tooltip={t("tooltip:title.markOutTimecode")}
        />
      </LeftControls>
      <TimetoolPreviewTimeline size="small" />
      <LeftControls direction="row" alignItems="center" spacing={0.5} display={linkedAction !== "" ? "none" : ""}>
        <Tooltip open={tooltipVisibility} title={t("common:addToSequence")}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleAddToSequence}
            onMouseOver={() => setTooltipVisibility(true)}
            onMouseLeave={() => setTooltipVisibility(false)}
          >
            <StartOutlined />
          </Button>
        </Tooltip>
      </LeftControls>
    </Stack>
  );
};

export default TimetoolSequenceEditor;

const LeftControls = styled(Stack)(({ theme }) => ({
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    border: "none",
    width: 32,
    height: 32,

    "&:after": {
      border: "1px ",
    },
  },
}));
